import { useEffect, useState } from 'preact/hooks';
import type { PrDetailBody } from '../../shared/pr/api';
import { currentStage, nextStep, type Viewer } from '../../shared/pr/workspace';
import { CancelRequest } from '../components/CancelRequest';
import { ChangeVertical } from '../components/ChangeVertical';
import { HeadDecision } from '../components/HeadDecision';
import { LinePrices } from '../components/LinePrices';
import { PettyCashRoute } from '../components/PettyCashRoute';
import { NextStepBar, RequestHeader, StageTabs } from '../components/Workspace';
import { useI18n, type MessageKey } from '../i18n';
import en from '../i18n/en.json';
import { ApiError, apiGet } from '../lib/api';
import { personName } from '../lib/people';
import { formatDateTime } from '../lib/dates';

// One request (requester view of mockup 08, without prices): where it is, what was asked, and its
// history from the audit log. The broker decides who may open it.

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; pr: PrDetailBody };

// Every audit action with a label (event.*) shows as words; anything else as a readable fallback.
const KNOWN_EVENTS = new Set(
  Object.keys(en)
    .filter((k) => k.startsWith('event.'))
    .map((k) => k.slice('event.'.length)),
);
const fallbackLabel = (action: string) =>
  action.charAt(0) + action.slice(1).toLowerCase().replace(/_/g, ' ');
const KNOWN_DECISIONS = new Set([
  'approve',
  'return',
  'reject',
  'sendToDeptHead',
  'headApprove',
  'headReturn',
  'headReject',
  'submitPO',
  'cfoApprove',
  'cfoReturn',
  'cfoReject',
  'sendFirst',
  'cfoSign',
]);

/** History entries shown before "Show full history". */
const LATEST = 3;

export function RequestDetailPage({
  prNo,
  procurement = false,
  cfo = false,
}: {
  prNo: string;
  procurement?: boolean;
  cfo?: boolean;
}) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [decided, setDecided] = useState(false);
  const [allHistory, setAllHistory] = useState(false);

  useEffect(() => {
    let live = true;
    apiGet<PrDetailBody>(`/api/prs/${encodeURIComponent(prNo)}/detail`)
      .then((pr) => live && setLoad({ kind: 'ok', pr }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [prNo, reload]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'not_found' ? t('pr.error.not_found') : t('req.loadError')}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
        <a class="button" href="#/requests">
          {t('req.back')}
        </a>
      </section>
    );
  }

  const { pr } = load;
  const viewer: Viewer = { staff: pr.staffView, procurement, cfo };
  const step = nextStep({
    status: pr.status,
    viewer,
    canEdit: pr.canEdit,
    canConfirmDelivery: pr.canConfirmDelivery,
    canRecordInvoice: pr.canRecordInvoice,
    hasPo: pr.hasPo,
    poNo: pr.poNo,
    headDecides: pr.actions.some((a) => a.startsWith('head')),
  });
  const category = pr.category
    ? lang === 'ar'
      ? pr.category.nameAR || pr.category.nameEN
      : pr.category.nameEN
    : '—';
  const facts: [MessageKey, string][] = [
    ['req.f.category', category],
    ['req.f.location', pr.location ? `${pr.location} (${pr.office})` : '—'],
  ];
  // Newest first: the latest few by default, all on request (like the review panel, D81).
  const newestFirst = [...pr.events].reverse();
  const events = allHistory ? newestFirst : newestFirst.slice(0, LATEST);
  const lastEvent = newestFirst[0];

  return (
    <div class="stack workspace">
      <RequestHeader pr={pr} />
      <StageTabs pr={pr} viewer={viewer} active={currentStage(pr.status, pr.staffView)} />
      {decided && (
        <p class="form-saved" role="status">
          {t('head.done')}
        </p>
      )}
      <NextStepBar pr={pr} step={step} />
      {step.primary === 'headDecide' && (
        <div id="head-decision">
          <HeadDecision
            pr={pr}
            onDone={() => {
              setDecided(true);
              setReload((r) => r + 1);
            }}
          />
        </div>
      )}
      <PettyCashRoute pr={pr} onDone={() => setReload((r) => r + 1)} />

      <div class="pr-layout">
        <div class="stack">
          <section class="card" aria-labelledby="d-details">
            <h3 id="d-details" class="card__title">
              {t('req.details')}
            </h3>
            <dl class="facts">
              {facts.map(([k, v]) => (
                <div key={k}>
                  <dt>{t(k)}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
            <h4 class="facts__label">{t('req.f.justification')}</h4>
            <p dir="auto">{pr.justification || '—'}</p>
            {pr.rush && (
              <>
                <h4 class="facts__label">{t('req.f.rushReason')}</h4>
                <p dir="auto">{pr.rushReason}</p>
              </>
            )}
          </section>

          <section class="card" aria-labelledby="d-items">
            <h3 id="d-items" class="card__title">
              {t('req.items')}
            </h3>
            {pr.lines.length === 0 ? (
              <p>—</p>
            ) : (
              <div class="table-wrap">
                <table class="table">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">{t('pr.line.description')}</th>
                      <th scope="col" class="num">
                        {t('pr.line.qty')}
                      </th>
                      <th scope="col">{t('pr.line.unit')}</th>
                      <th scope="col">{t('pr.line.type')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pr.lines.map((l) => (
                      <tr key={l.lineNo}>
                        <td class="ref" dir="ltr">
                          {String(l.lineNo).padStart(2, '0')}
                        </td>
                        <td dir="auto">
                          {l.description}
                          {l.specs && <span class="caption cell-sub">{l.specs}</span>}
                        </td>
                        <td class="num" dir="ltr">
                          {l.qty}
                        </td>
                        <td>{t(`pr.unit.${l.unit}` as MessageKey)}</td>
                        <td>{t(`pr.lineType.${l.lineType}` as MessageKey)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {procurement && <LinePrices pr={pr} />}

          {pr.files.length > 0 && (
            <section class="card" aria-labelledby="d-files">
              <h3 id="d-files" class="card__title">
                {t('req.files')}
              </h3>
              <ul class="pr-files">
                {pr.files.map((f) => (
                  <li key={f} dir="auto">
                    {f}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div class="stack">
          <section class="card" aria-labelledby="d-who">
            <h3 id="d-who" class="card__title">
              {t('ws.who.title')}
            </h3>
            <dl class="ws-who">
              <div>
                <dt>{t('ws.who.now')}</dt>
                <dd>{t(`ws.owner.${step.owner}` as MessageKey)}</dd>
              </div>
              {lastEvent && (
                <div>
                  <dt>{t('ws.who.last')}</dt>
                  <dd>{formatDateTime(lastEvent.when, lang)}</dd>
                </div>
              )}
            </dl>
            {pr.canCancel && <CancelRequest pr={pr} onDone={() => setReload((r) => r + 1)} />}
            <ChangeVertical pr={pr} onDone={() => setReload((r) => r + 1)} />
          </section>
          {pr.decisions.some((d) => d.remark) && (
            <section class="card" aria-labelledby="d-remarks">
              <h3 id="d-remarks" class="card__title">
                {t('req.remarks')}
              </h3>
              <ol class="timeline">
                {pr.decisions
                  .filter((d) => d.remark)
                  .map((d) => (
                    <li key={d.step}>
                      <span class="timeline__what">
                        {KNOWN_DECISIONS.has(d.decision)
                          ? t(`decision.${d.decision}` as MessageKey)
                          : d.decision}
                      </span>
                      <span dir="auto">{d.remark}</span>
                      <span class="caption">
                        {formatDateTime(d.when, lang)} · <span dir="ltr">{d.by}</span>
                      </span>
                    </li>
                  ))}
              </ol>
            </section>
          )}
          <aside class="card" aria-labelledby="d-history">
            <h3 id="d-history" class="card__title">
              {allHistory ? t('req.history') : t('ws.history.latest')}
            </h3>
            {pr.events.length === 0 ? (
              <p>—</p>
            ) : (
              <ol class="timeline">
                {events.map((e, i) => (
                  <li key={`${e.when}-${i}`}>
                    <span class="timeline__what">
                      {KNOWN_EVENTS.has(e.action)
                        ? t(`event.${e.action}` as MessageKey)
                        : fallbackLabel(e.action)}
                    </span>
                    <span class="caption">
                      {formatDateTime(e.when, lang)} ·{' '}
                      <span title={e.who}>{personName(e.who)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
            {pr.events.length > LATEST && (
              <button
                type="button"
                class="button button--small review-history__toggle"
                aria-expanded={allHistory}
                onClick={() => setAllHistory((v) => !v)}
              >
                {allHistory
                  ? t('review.history.less')
                  : t('review.history.more', { n: String(pr.events.length) })}
              </button>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
